<template>
  <div class="about-component">
    <template v-if="_logoAppear">
      <div
        class="logo-caption"
        :style="{
          top: `${this.$data.logoCaptionY}`,
          left: `${this.$data.logoCaptionX}`,
        }"
      >
        The mundi logo will appear in a balanced environment.
      </div>
    </template>

    <div class="background" :class="{ 'background--show': isMenuOpen }" @click="onClick" />

    <div class="about" :class="{ 'about--show': isMenuOpen }">
      <div class="wrapper">
        <div class="close-button" @click="onClick">
          <img :src="_buttonSvgs.CLOSE_BUTTON" />
        </div>
        <div class="top">
          <div class="logo" @mouseover="onLogoMouseover" @mousemove="onLogoMousemove" @mouseleave="onLogoMouseleave">
            <img :src="_logoSrc" />
          </div>
          <div class="explanation">
            Below are the key notes to understand the Mundi concept and how it works.
          </div>
        </div>

        <div class="topic">
          <div class="topic-label">agents</div>
          <div class="paragraph">
            <div class="par-label">
              <img :src="_agentSvgs.AGENT_1_SVG" />
              <img :src="_agentSvgs.AGENT_1_INVERSION_SVG" />
            </div>
            <div class="par-content">
              <template v-if="_isPC">
                <p>
                  type: <span class="bold">agent_1</span>, max_population: {{ _population[0].max }}
                  The lowest class agent. Does not require any special conditions for birth. Simply input the birth
                  rate.
                </p>
              </template>
              <template v-else>
                <p>type: <span class="bold">agent_1</span>, max_population: {{ _population[0].max }}</p>
                <p>
                  Lower class agent. Does not require any special conditions for birth. Simply input the birth rate.
                </p>
              </template>
            </div>
          </div>
          <div class="paragraph">
            <div class="par-label">
              <img :src="_agentSvgs.AGENT_2_INVERSION_SVG" />
              <img :src="_agentSvgs.AGENT_2_SVG" />
            </div>
            <div class="par-content">
              <template v-if="_isPC">
                <p>
                  type: <span class="bold">agent_2</span>, max_population: {{ _population[1].max }}
                  Lower class agent. The condition for birth is that the population of
                  <span class="bold">agent_1</span> is <span class="bold">{{ _agentCondition.agent_2 }}</span
                  >.
                </p>
              </template>
              <template v-else>
                <p>type: <span class="bold">agent_2</span>, max_population: {{ _population[1].max }}</p>
                <p>
                  Lower class agent. The condition for birth is that the population of
                  <span class="bold">agent_1</span> is <span class="bold">{{ _agentCondition.agent_2 }}</span
                  >.
                </p>
              </template>
            </div>
          </div>
          <div class="paragraph">
            <div class="par-label">
              <img :src="_agentSvgs.AGENT_3_3_SVG" />
              <img :src="_agentSvgs.AGENT_3_4_SVG" />
              <img :src="_agentSvgs.AGENT_3_1_SVG" />
            </div>
            <div class="par-content">
              <template v-if="_isPC">
                <p>
                  type: <span class="bold">agent_3</span>, max_population: {{ _population[2].max }}
                  Middle class agent. The condition for birth is that the population of
                  <span class="bold">agent_2</span> is <span class="bold">{{ _agentCondition.agent_3 }}</span
                  >.
                </p>
              </template>
              <template v-else>
                <p>type: <span class="bold">agent_3</span>, max_population: {{ _population[2].max }}</p>
                <p>
                  Middle class agent. The condition for birth is that the population of
                  <span class="bold">agent_2</span> is <span class="bold">{{ _agentCondition.agent_3 }}</span
                  >.
                </p>
              </template>
            </div>
          </div>
          <div class="paragraph">
            <div class="par-label">
              <img :src="_agentSvgs.AGENT_4_SVG" />
            </div>
            <div class="par-content">
              <template v-if="_isPC">
                <p>
                  type: <span class="bold">agent_4</span>, max_population: {{ _population[3].max }}
                  Highest class agent. The condition for birth is that the population of
                  <span class="bold">agent_3</span> is <span class="bold">{{ _agentCondition.agent_4 }}</span
                  >.
                </p>
              </template>
              <template v-else>
                <p>type: <span class="bold">agent_4</span>, max_population: {{ _population[3].max }}</p>
                <p>
                  Highest class agent. The condition for birth is that the population of
                  <span class="bold">agent_3</span> is <span class="bold">{{ _agentCondition.agent_4 }}</span
                  >.
                </p>
              </template>
            </div>
          </div>
        </div>

        <div class="topic">
          <div class="topic-label">functions</div>
          <div class="paragraph">
            <div class="par-label">
              <img :src="_buttonSvgs.PLAY_BUTTON" />
              <img :src="_buttonSvgs.PLAY_BUTTON_HOVER" />
            </div>
            <div class="par-content">
              <template v-if="_isPC">
                <p><span class="bold">Play</span> button. Play the drone sound source.</p>
              </template>
              <template v-else>
                <p><span class="bold">Play</span> button</p>
                <p>Play the drone sound source.</p>
              </template>
            </div>
          </div>
          <div class="paragraph">
            <div class="par-label">
              <img :src="_buttonSvgs.PAUSE_BUTTON_HOVER" />
              <img :src="_buttonSvgs.PAUSE_BUTTON" />
            </div>
            <div class="par-content">
              <template v-if="_isPC">
                <p><span class="bold">Pause</span> button. Pause the drone sound source.</p>
              </template>
              <template v-else>
                <p><span class="bold">Pause</span> button</p>
                <p>Pause the drone sound source.</p>
              </template>
            </div>
          </div>
          <div class="paragraph">
            <div class="par-label">
              <img :src="_buttonSvgs.RESET_BUTTON" />
              <img :src="_buttonSvgs.RESET_BUTTON_HOVER" />
            </div>
            <div class="par-content">
              <template v-if="_isPC">
                <p><span class="bold">Reset</span> button. Reset the location of agents</p>
              </template>
              <template v-else>
                <p><span class="bold">Reset</span> button</p>
                <p>Reset the location of agents</p>
              </template>
            </div>
          </div>
          <div class="paragraph">
            <div class="par-label">
              <img :src="_buttonSvgs.RESTORE_BUTTON_HOVER" />
              <img :src="_buttonSvgs.RESTORE_BUTTON" />
            </div>
            <div class="par-content">
              <template v-if="_isPC">
                <p><span class="bold">Restore</span> button. Restore the birth rate and the death rate.</p>
              </template>
              <template v-else>
                <p><span class="bold">Restore</span> button</p>
                <p>Restore the birth rate and the death rate.</p>
              </template>
            </div>
          </div>
        </div>

        <div class="topic">
          <div class="topic-label">Eclipse</div>
          <div class="paragraph">
            <div class="par-label">
              <p>00:00, 15:00, 30:00, 45:00</p>
            </div>
            <div class="par-content">
              <template v-if="_isPC">
                <p>
                  Solar eclipses occur frequently in the Mundi. An eclipse is scheduled every 15 minutes. All variables
                  related to agent's behavior are affected by eclipse.
                </p>
              </template>
              <template v-else>
                <p>Solar eclipses occur frequently in the Mundi. An eclipse is scheduled every 15 minutes.</p>
                <p>All variables related to agent's behavior are affected by eclipse.</p>
              </template>
            </div>
          </div>
        </div>

        <div class="topic">
          <div class="topic-label">Credit</div>
          <div class="paragraph paragraph--creator">
            <div class="par-label">
              <p>designed by</p>
            </div>
            <div class="par-content">
              <p><span class="bold">Yuki Agatsuma</span> and <span class="bold">Wataru Iwata</span></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LOGO_SIDE from '../assets/logo/logo-side.svg';
import AGENT_SVGS from '../assets/images/agents/index.js';
import CONSTANS from '@/constants/index.js';
import BUTTON_SVGS from '../assets/images/buttons/index.js';

export default {
  props: {
    isMenuOpen: Boolean,
    worldWidth: Number,
  },
  data() {
    return {
      isLogoCaptionAppear: false,
      logoCaptionX: null,
      logoCaptionY: null,
    };
  },
  methods: {
    onClick() {
      this.$emit('overlayClick');
    },
    onLogoMouseover(event) {
      this.$data.isLogoCaptionAppear = true;
      this.$data.logoCaptionX = `${event.clientX + 3}px`;
      this.$data.logoCaptionY = `${event.clientY + 3}px`;
    },
    onLogoMousemove(event) {
      this.$data.logoCaptionX = `${event.clientX + 3}px`;
      this.$data.logoCaptionY = `${event.clientY + 3}px`;
    },
    onLogoMouseleave() {
      this.$data.isLogoCaptionAppear = false;
    },
  },
  computed: {
    _logoAppear() {
      return this.$data.isLogoCaptionAppear;
    },
    _logoSrc() {
      return LOGO_SIDE;
    },
    _agentSvgs() {
      return AGENT_SVGS;
    },
    _buttonSvgs() {
      return BUTTON_SVGS;
    },
    _population() {
      return CONSTANS.TYPE_MAP;
    },
    _agentCondition() {
      return {
        agent_2: `between ${CONSTANS.BIRTH_CONDITION.agent_2.min * 100}% and ${CONSTANS.BIRTH_CONDITION.agent_2.max *
          100}%`,
        agent_3: `between ${CONSTANS.BIRTH_CONDITION.agent_3.min * 100}% and ${CONSTANS.BIRTH_CONDITION.agent_3.max *
          100}%`,
        agent_4: `between ${CONSTANS.BIRTH_CONDITION.agent_4.min * 100}% and ${CONSTANS.BIRTH_CONDITION.agent_4.max *
          100}%`,
      };
    },
    _isPC() {
      const pcBreakPoint = 901;

      return this.$props.worldWidth >= pcBreakPoint;
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.about-component {
  width: 100%;
  height: 100%;
  position: relative;
  color: rgba(7, 7, 81, 0.9);
}

.logo-caption {
  position: absolute;
  width: 300px;
  height: 20px;
  border: 1px solid rgb(7, 7, 81);
  background-color: rgb(255, 255, 255);
  border-radius: 3px;
  z-index: 12;
  display: flex;
  align-items: center;
  justify-content: center;

  animation: fadeIn 0.2s ease-in-out alternate;

  @include sp {
    width: 150px;
    height: 10px;
    padding: 10px;
  }
}

.background {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0px;
  z-index: 10;
  background-color: rgb(255, 255, 255);
  opacity: 0;
  cursor: pointer;

  transition: all 0.09s;
  transition-timing-function: cubic-bezier(0.17, 0.935, 0.305, 1);
  visibility: hidden;

  &--show {
    opacity: 0.6;
    visibility: visible;
  }
}

.about {
  position: fixed;
  top: 0;
  left: -105%;
  z-index: 11;

  width: calc(89% - 2px); // boeder表示分でひく
  height: calc(100% - 2px); // boeder表示分でひく

  @media only screen and (min-width: 1150px) {
    width: calc(1100px - 2px); // boeder表示分でひく
  }

  overflow-y: scroll;

  background-color: rgb(255, 255, 255);

  border: solid rgb(7, 7, 81);
  border-width: 1px 1px 1px 1px;

  // border-top-right-radius: 10% 25%;
  // border-bottom-right-radius: 10% 25%;
  border-top-right-radius: 0%;
  border-bottom-right-radius: 0%;

  // 戻る時
  transition: left 0.1s cubic-bezier(0.17, 0.935, 0.305, 1) 0s,
    border-top-right-radius 0.2s cubic-bezier(0.17, 0.935, 0.305, 1) 0s,
    border-bottom-right-radius 0.2s cubic-bezier(0.17, 0.935, 0.305, 1) 0s;

  display: flex;
  align-items: center;

  &--show {
    // 出る時
    transition: left 0.1s cubic-bezier(0.17, 0.935, 0.305, 1) 0s,
      border-top-right-radius 0.15s cubic-bezier(0.17, 0.935, 0.305, 1) 0.03s,
      border-bottom-right-radius 0.15s cubic-bezier(0.17, 0.935, 0.305, 1) 0.03s;

    border-top-right-radius: 149px 58%;
    border-bottom-right-radius: 229px 38%;

    left: 0px;
  }

  @include tb {
    width: calc(100% - 2px); // boeder表示分でひく

    top: -105%;
    left: 0%;
    align-items: center;

    border-top-right-radius: 0px 0%;
    border-bottom-right-radius: 0% 0%;

    transition: top 0.1s cubic-bezier(0.17, 0.935, 0.305, 1) 0.2s,
      border-bottom-right-radius 0.2s cubic-bezier(0.17, 0.935, 0.305, 1) 0s;

    &--show {
      transition: top 0.1s cubic-bezier(0.17, 0.935, 0.305, 1) 0s,
        border-bottom-right-radius 0.5s cubic-bezier(0.17, 0.935, 0.305, 1) 0.1s;
      border-bottom-right-radius: 30% 30%;
      top: 0px;
    }
  }

  @include sp {
    width: calc(100% - 2px); // boeder表示分でひく

    top: -105%;
    left: 0%;
    align-items: unset;

    border-top-right-radius: 0px 0%;
    border-bottom-right-radius: 0% 0%;

    transition: top 0.1s cubic-bezier(0.17, 0.935, 0.305, 1) 0.2s,
      border-bottom-right-radius 0.2s cubic-bezier(0.17, 0.935, 0.305, 1) 0s;

    &--show {
      transition: top 0.1s cubic-bezier(0.17, 0.935, 0.305, 1) 0s,
        border-bottom-right-radius 0.5s cubic-bezier(0.17, 0.935, 0.305, 1) 0.1s;
      border-bottom-right-radius: 18% 15%;
      top: 0px;
    }
  }
}

.wrapper {
  width: 100%;
  margin-bottom: 10px;
}

.close-button {
  position: absolute;
  top: 0px;
  left: 0px;
  padding: 10px;
  width: 12px;
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.32, 0.625, 0.58, 1);
  display: flex;
  align-items: center;

  &:hover {
    transform: rotate(180deg);
    transform-origin: center;
  }

  img {
    width: 100%;
    height: 100%;
  }
}

.top {
  margin: 0 20px 20px;
}

.logo {
  width: 200px;
  height: 100px;
  margin-bottom: 5px;
  cursor: help;

  img {
    width: 100%;
    height: 100%;
  }
}

.explanation {
  font-size: 14px;
}

.topic {
  margin: 10px 20px 20px;

  &:last-child {
    margin: 10px 20px 30px;
  }
}

.topic-label {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 20px;
}

.paragraph {
  display: flex;
  align-items: center;
  margin-bottom: 17px;

  &--creator {
    margin-bottom: 5px;
  }
}

.par-label {
  flex: 0 0 170px;
  display: flex;
  margin-right: 20px;
  justify-content: center;

  @include tb {
    flex: 0 0 80px;
  }

  @include sp {
    flex: 0 0 80px;
  }

  img {
    width: 30px;
    margin: 0 10px;

    @include tb {
      width: 20px;

      &:nth-child(3) {
        display: none;
      }
    }

    @include sp {
      width: 20px;

      &:nth-child(3) {
        display: none;
      }
    }
  }
}

.par-content {
  flex: 1 1 0;

  p {
    margin-bottom: 5px;
  }
}

.bold {
  font-weight: bold;
}

.under-line {
  border-bottom: solid 1px rgb(7, 7, 81);
}
</style>
